import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      setError(null);
    } catch (err) {
      setUser(null);
      // Only set error if it's not a 401 (expected when not logged in)
      if (err.response?.status !== 401) {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async () => {
    try {
      // Get Google OAuth URL from backend
      const response = await axios.get(`${API_URL}/api/auth/google`, {
        withCredentials: true
      });
      // Redirect to Google OAuth
      window.location.href = response.data.auth_url;
    } catch (err) {
      console.error('Failed to get Google auth URL:', err);
      setError('Failed to initiate Google login');
    }
  };

  const processGoogleCallback = async (code, state) => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${API_URL}/api/auth/google/callback`,
        { code, state },
        { withCredentials: true }
      );
      setUser(response.data.user);
      setError(null);
      return response.data.user;
    } catch (err) {
      setError(err.response?.data?.detail || 'Authentication failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await axios.patch(
        `${API_URL}/api/users/profile`,
        profileData,
        { withCredentials: true }
      );
      setUser(response.data);
      return response.data;
    } catch (err) {
      throw err;
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    processGoogleCallback,
    updateProfile,
    checkAuth,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
